<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const sinceDate = ref('');
const untilDate = ref('');
const selectedInboxId = ref('');
const currentPage = ref(1);
const hasError = ref(false);

const callLogs = computed(
  () => getters['phoneCentralConfigs/getCallLogs'].value
);
const uiFlags = computed(
  () => getters['phoneCentralConfigs/getUIFlags'].value
);
const inboxes = computed(() => getters['inboxes/getInboxes'].value);

const tableHeaders = computed(() => [
  t('PHONE_CENTRAL.CALL_LOGS.TABLE.DATE'),
  t('PHONE_CENTRAL.CALL_LOGS.TABLE.PHONE'),
  t('PHONE_CENTRAL.CALL_LOGS.TABLE.DIALED'),
  t('PHONE_CENTRAL.CALL_LOGS.TABLE.EXTENSION'),
  t('PHONE_CENTRAL.CALL_LOGS.TABLE.STATUS'),
  t('PHONE_CENTRAL.CALL_LOGS.TABLE.DURATION'),
  t('PHONE_CENTRAL.CALL_LOGS.TABLE.CONTACT'),
  t('PHONE_CENTRAL.CALL_LOGS.TABLE.CONVERSATION'),
]);

const meta = computed(() => callLogs.value?.meta || {});
const records = computed(() => callLogs.value?.data || []);
const totalPages = computed(() => meta.value.total_pages || 0);
const totalCount = computed(() => meta.value.total_count || 0);

const fetchLogs = async () => {
  hasError.value = false;
  try {
    await store.dispatch('phoneCentralConfigs/getCallLogs', {
      page: currentPage.value,
      since: sinceDate.value || undefined,
      until: untilDate.value || undefined,
      inboxId: selectedInboxId.value || undefined,
    });
  } catch (error) {
    hasError.value = true;
    useAlert(t('PHONE_CENTRAL.CALL_LOGS.ERROR'));
  }
};

const applyFilter = () => {
  currentPage.value = 1;
  fetchLogs();
};

const goToPage = page => {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  fetchLogs();
};

const formatDate = dateStr => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push('...');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (current < total - 2) pages.push('...');
    pages.push(total);
  }
  return pages;
});

watch(selectedInboxId, () => applyFilter());

onBeforeMount(() => {
  store.dispatch('inboxes/get');
  fetchLogs();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Filters -->
    <div class="flex flex-wrap items-end gap-4 p-4 rounded-lg bg-n-background border border-n-weak">
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('PHONE_CENTRAL.CALL_LOGS.FILTER.SINCE') }}
        </label>
        <input
          v-model="sinceDate"
          type="date"
          class="h-8 px-2 text-sm border rounded border-n-weak bg-n-background"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('PHONE_CENTRAL.CALL_LOGS.FILTER.UNTIL') }}
        </label>
        <input
          v-model="untilDate"
          type="date"
          class="h-8 px-2 text-sm border rounded border-n-weak bg-n-background"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('PHONE_CENTRAL.CALL_LOGS.FILTER.INBOX') }}
        </label>
        <select
          v-model="selectedInboxId"
          class="h-8 px-2 text-sm border rounded border-n-weak bg-n-background mb-0"
        >
          <option value="">
            {{ $t('PHONE_CENTRAL.CALL_LOGS.FILTER.ALL_INBOXES') }}
          </option>
          <option
            v-for="inbox in inboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <Button
        :label="$t('PHONE_CENTRAL.CALL_LOGS.FILTER.APPLY')"
        size="sm"
        @click="applyFilter"
      />
    </div>

    <!-- Summary -->
    <div v-if="totalCount > 0" class="text-sm text-n-slate-11">
      {{ $t('PHONE_CENTRAL.CALL_LOGS.TOTAL', { count: totalCount }) }}
    </div>

    <!-- Loading -->
    <div
      v-if="uiFlags.isFetchingCallLogs"
      class="flex items-center justify-center py-8"
    >
      <span class="text-n-slate-11">
        {{ $t('PHONE_CENTRAL.CALL_LOGS.LOADING') }}
      </span>
    </div>

    <!-- Table -->
    <div v-else-if="records.length">
      <BaseTable
        :headers="tableHeaders"
        :items="records"
        :no-data-message="$t('PHONE_CENTRAL.CALL_LOGS.EMPTY')"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="log in items"
            :key="log.id"
            :item="log"
          >
            <template #default>
              <BaseTableCell>
                <span class="text-xs text-n-slate-12 whitespace-nowrap">
                  {{ formatDate(log.created_at) }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-xs text-n-slate-12 font-medium">
                  {{ log.telefone || '-' }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-xs text-n-slate-11">
                  {{ log.discado || '-' }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-xs text-n-slate-11">
                  {{ log.extensao || '-' }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                  :class="{
                    'bg-n-jade-2 text-n-jade-11': log.status === 'ANSWERED',
                    'bg-n-ruby-2 text-n-ruby-11': log.status === 'NO ANSWER' || log.status === 'FAILED',
                    'bg-n-amber-2 text-n-amber-11': log.status === 'BUSY',
                    'bg-n-slate-3 text-n-slate-11': !['ANSWERED', 'NO ANSWER', 'FAILED', 'BUSY'].includes(log.status),
                  }"
                >
                  {{ log.status || '-' }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-xs text-n-slate-11">
                  {{ log.duracao_chamada || '-' }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-xs text-n-slate-12">
                  {{ log.contact_name || '-' }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <a
                  v-if="log.conversation_id"
                  :href="`/app/accounts/${$route.params.accountId}/conversations/${log.conversation_id}`"
                  class="text-xs text-n-brand-11 hover:underline"
                >
                  #{{ log.conversation_id }}
                </a>
                <span v-else class="text-xs text-n-slate-11">-</span>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>

      <!-- Pagination -->
      <div
        v-if="totalPages > 1"
        class="flex items-center justify-between mt-4 px-2"
      >
        <span class="text-xs text-n-slate-11">
          {{ $t('PHONE_CENTRAL.CALL_LOGS.PAGE_INFO', {
            current: meta.current_page,
            total: totalPages,
          }) }}
        </span>

        <div class="flex items-center gap-1">
          <Button
            icon="i-lucide-chevron-left"
            slate
            xs
            :disabled="currentPage <= 1"
            @click="goToPage(currentPage - 1)"
          />

          <template v-for="(page, idx) in visiblePages" :key="idx">
            <span
              v-if="page === '...'"
              class="px-2 text-xs text-n-slate-11"
            >
              ...
            </span>
            <button
              v-else
              class="h-7 min-w-[28px] px-1.5 text-xs rounded"
              :class="
                page === currentPage
                  ? 'bg-n-brand-9 text-white'
                  : 'text-n-slate-11 hover:bg-n-slate-3'
              "
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </template>

          <Button
            icon="i-lucide-chevron-right"
            slate
            xs
            :disabled="currentPage >= totalPages"
            @click="goToPage(currentPage + 1)"
          />
        </div>
      </div>
    </div>

    <!-- Error state -->
    <div
      v-else-if="hasError"
      class="flex items-center justify-center py-12 text-n-ruby-11"
    >
      {{ $t('PHONE_CENTRAL.CALL_LOGS.ERROR') }}
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ $t('PHONE_CENTRAL.CALL_LOGS.EMPTY') }}
    </div>
  </div>
</template>
